import React, { useState, useRef } from 'react';
import { Upload, Image, Sparkles, ArrowRight, X } from 'lucide-react';
import { useLang } from '../../i18n/LangContext';
import { AI_PROJECT_TYPES, AI_STYLES } from '../../data/siteData';

export default function AIVisualizer() {
  const { t } = useLang();
  const [step, setStep] = useState(1);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [projectType, setProjectType] = useState('');
  const [style, setStyle] = useState('');
  const fileRef = useRef(null);

  const handlePhoto = (e) => { const f = e.target.files?.[0]; if (!f) return; setPhoto(f); setPhotoPreview(URL.createObjectURL(f)); setStep(2); };
  const handleGenerate = () => { if (projectType && style) setStep(4); };
  const reset = () => { setStep(1); setPhoto(null); setPhotoPreview(null); setProjectType(''); setStyle(''); };
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="visualizer" className="bg-[#12151A] py-20 sm:py-28">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#D4AF37] text-xs font-mono tracking-[0.25em] uppercase">{t.visualizer.label}</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-3 font-outfit">{t.visualizer.heading}</h2>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">{t.visualizer.desc}</p>
        </div>
        <div className="bg-[#0B0D0F] rounded-lg border border-white/8 overflow-hidden">
          <div className="flex border-b border-white/8">
            {t.visualizer.steps.map((s, i) => (
              <div key={i} className={`flex-1 py-3 px-4 text-center text-xs sm:text-sm border-r border-white/8 last:border-r-0 ${step >= i + 1 ? 'text-[#D4AF37] bg-[#D4AF37]/5' : 'text-slate-500'}`}>
                <span className="font-mono mr-1">{i + 1}.</span> {s}
              </div>
            ))}
          </div>
          <div className="p-6 sm:p-10">
            {step === 1 && (
              <div className="text-center py-12">
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/jpg,image/heic" onChange={handlePhoto} className="hidden" />
                <button onClick={() => fileRef.current?.click()} data-testid="ai-upload-btn"
                  className="inline-flex flex-col items-center gap-4 border-2 border-dashed border-white/20 hover:border-[#D4AF37]/40 rounded-lg p-12 transition-colors cursor-pointer">
                  <Upload className="w-12 h-12 text-slate-500" />
                  <div><p className="text-white font-semibold mb-1">{t.visualizer.upload}</p><p className="text-slate-400 text-sm">{t.visualizer.uploadHint}</p></div>
                </button>
              </div>
            )}
            {step === 2 && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  {photoPreview && <div className="flex items-center gap-3"><img src={photoPreview} alt="Uploaded" className="w-16 h-16 rounded object-cover" /><span className="text-slate-400 text-sm">{photo?.name}</span></div>}
                  <button onClick={reset} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <h3 className="text-white font-semibold mb-4 font-outfit">{t.visualizer.selectType}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {AI_PROJECT_TYPES.map(type => (
                    <button key={type} onClick={() => { setProjectType(type); setStep(3); }}
                      className={`p-3 rounded border text-sm text-left transition-all ${projectType === type ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-white' : 'border-white/10 text-slate-300 hover:border-white/20'}`}>{type}</button>
                  ))}
                </div>
              </div>
            )}
            {step === 3 && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">{photoPreview && <img src={photoPreview} alt="Uploaded" className="w-12 h-12 rounded object-cover" />}<div className="text-sm"><span className="text-slate-400">Type:</span><span className="text-white ml-2">{projectType}</span></div></div>
                  <button onClick={reset} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <h3 className="text-white font-semibold mb-4 font-outfit">{t.visualizer.selectStyle}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  {AI_STYLES.map(s => (
                    <button key={s} onClick={() => setStyle(s)}
                      className={`p-4 rounded border text-sm text-center transition-all ${style === s ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-white' : 'border-white/10 text-slate-300 hover:border-white/20'}`}>{s}</button>
                  ))}
                </div>
                {style && (
                  <button onClick={handleGenerate} data-testid="ai-generate-btn"
                    className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E5C158] text-black font-bold py-4 rounded transition-all">
                    <Sparkles className="w-5 h-5" />{t.visualizer.generate}
                  </button>
                )}
              </div>
            )}
            {step === 4 && (
              <div className="text-center py-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                  <div><p className="text-slate-400 text-xs font-mono uppercase mb-2">{t.visualizer.upload}</p><div className="rounded-lg overflow-hidden border border-white/10">{photoPreview && <img src={photoPreview} alt="Original" className="w-full aspect-[4/3] object-cover" />}</div></div>
                  <div><p className="text-slate-400 text-xs font-mono uppercase mb-2">AI Visualization</p><div className="rounded-lg border border-[#D4AF37]/20 bg-[#12151A] flex items-center justify-center aspect-[4/3]"><div className="text-center p-6"><Image className="w-16 h-16 text-[#D4AF37]/30 mx-auto mb-4" /><p className="text-[#D4AF37] font-semibold mb-2">{t.visualizer.comingSoon}</p><p className="text-slate-500 text-sm">{t.visualizer.comingDesc}<br />{t.visualizer.estimateAfter} {projectType.toLowerCase()} {t.visualizer.project}</p></div></div></div>
                </div>
                <div className="bg-[#12151A] rounded-lg border border-[#D4AF37]/20 p-6 mb-6">
                  <p className="text-white font-semibold mb-1 font-outfit">{t.visualizer.loveIt}</p>
                  <p className="text-slate-400 text-sm mb-4">{t.visualizer.letUs}</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button onClick={() => scrollTo('#estimate')} data-testid="ai-estimate-cta" className="bg-[#D4AF37] hover:bg-[#E5C158] text-black font-bold px-6 py-3 rounded transition-all">{t.hero.cta1}</button>
                    <button onClick={reset} className="border border-white/20 text-white px-6 py-3 rounded hover:bg-white/5 transition-all">{t.visualizer.tryAnother}</button>
                  </div>
                </div>
                <p className="text-slate-500 text-xs italic max-w-lg mx-auto">{t.visualizer.disclaimer}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
